import {
  Autocomplete,
  FormHelperText,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { FC, useMemo } from 'react'
import { useQueryMasters } from 'src/features/masters/api/useQueryMasters'
import { useMasterSelection } from 'src/features/templateEdit/hooks/useMasterSelection'
import { OptionToggleSwitch } from 'src/features/templateEdit/templateOptions/OptionToggleSwitch'
import { masterSelectorStyles } from './masterSelector.style'

export type MasterSelectorProps = {
  value?: string | null
  onChange: (masterUuid: string | null) => void
  onToggleChange: (isMasterEnabled: boolean) => void
  isMasterEnabled: boolean
  hasQuestionsWithMasterFieldReferences: boolean
  disabled?: boolean
  externalError?: string
}

export const MasterSelector: FC<MasterSelectorProps> = ({
  value,
  onChange,
  onToggleChange,
  isMasterEnabled,
  hasQuestionsWithMasterFieldReferences,
  disabled = false,
  externalError = '',
}) => {
  const { data: mastersResponse, isLoading, error } = useQueryMasters()
  const {
    isToggleOn,
    handleToggleChange,
    selectedMaster,
    handleMasterChange,
    isToggleDisabled,
    isMasterChangeDisabled,
    disabledMessage,
  } = useMasterSelection({
    value,
    onChange,
    onToggleChange,
    isMasterEnabled,
    hasQuestionsWithMasterFieldReferences,
  })

  const masters = useMemo(() => {
    return mastersResponse?.masters ?? []
  }, [mastersResponse])

  const selectedMasterOption = useMemo(() => {
    if (!selectedMaster) return null
    return masters.find(m => m.uuid === selectedMaster) ?? null
  }, [selectedMaster, masters])

  // エラー状態の判定: 外部エラーのみ（保存ボタン押下時に設定される）
  const errorMessage = externalError
  const hasError = Boolean(errorMessage)

  return (
    <Stack sx={masterSelectorStyles.container} id="master-selector-error">
      {/* Toggle Switch with Label */}
      <Tooltip
        title={isToggleDisabled ? disabledMessage : ''}
        placement="top"
        arrow
      >
        <span>
          <OptionToggleSwitch
            isChecked={isToggleOn}
            onChange={handleToggleChange}
            label="製品マスタと紐づける"
            isDisabled={disabled || isToggleDisabled}
          />
        </span>
      </Tooltip>

      {/* Master Autocomplete (表示条件: Toggle ON) */}
      {isToggleOn && (
        <Stack spacing={0}>
          <Tooltip
            title={isMasterChangeDisabled ? disabledMessage : ''}
            placement="top"
            arrow
          >
            <Autocomplete
              value={selectedMasterOption}
              onChange={(_event, newValue) => {
                handleMasterChange(newValue?.uuid ?? null)
              }}
              options={masters}
              getOptionLabel={option => option.name}
              loading={isLoading}
              disabled={disabled || isMasterChangeDisabled}
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  error={hasError}
                  required
                  variant="outlined"
                  size="small"
                  placeholder="製品マスタを選択"
                  data-testid="master-selector-autocomplete"
                />
              )}
              noOptionsText={
                error ? 'データの読み込みに失敗しました' : 'データがありません'
              }
              sx={masterSelectorStyles.autocomplete}
            />
          </Tooltip>

          {/* Error Message */}
          {hasError && (
            <FormHelperText error data-testid="master-selector-error-text">
              {errorMessage}
            </FormHelperText>
          )}
        </Stack>
      )}
    </Stack>
  )
}
